import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Save, AlertCircle } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EmailConfig {
  email_resend_api_key: string;
  email_sender_email: string;
  email_sender_name: string;
  email_admin_email: string;
  email_enabled: boolean;
}

const AdminEmailSettings = () => {
  const [config, setConfig] = useState<EmailConfig>({
    email_resend_api_key: '',
    email_sender_email: '',
    email_sender_name: '',
    email_admin_email: '',
    email_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('key, value')
        .in('key', [
          'email_resend_api_key',
          'email_sender_email', 
          'email_sender_name',
          'email_admin_email',
          'email_enabled'
        ]);

      if (error) throw error;

      const configObj: any = {};
      data?.forEach(item => {
        if (item.key === 'email_enabled') {
          configObj[item.key] = item.value === true || item.value === 'true';
        } else {
          configObj[item.key] = typeof item.value === 'string' ? item.value.replace(/"/g, '') : item.value;
        }
      });

      setConfig(configObj as EmailConfig);
    } catch (error) {
      console.error('Error fetching email config:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la configuración de correo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const normalize = (s: string) => (s ?? '').trim().replace(/^([\"'])(.*)\1$/g, '$2');
      const updates = [
        { key: 'email_resend_api_key', value: normalize(config.email_resend_api_key) },
        { key: 'email_sender_email', value: normalize(config.email_sender_email) },
        { key: 'email_sender_name', value: normalize(config.email_sender_name) },
        { key: 'email_admin_email', value: normalize(config.email_admin_email) },
        { key: 'email_enabled', value: config.email_enabled },
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('site_config')
          .update({ value: update.value, updated_at: new Date().toISOString() })
          .eq('key', update.key);

        if (error) throw error;
      }

      toast({
        title: "Configuración guardada",
        description: "La configuración de correo se guardó exitosamente",
      });
    } catch (error) {
      console.error('Error saving config:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!config.email_resend_api_key || !config.email_sender_email || !config.email_admin_email) {
      toast({
        title: "Configuración incompleta",
        description: "Por favor completa la configuración antes de enviar un correo de prueba",
        variant: "destructive",
      });
      return;
    }

    setTesting(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-test-email', {
        body: {
          resend_api_key: config.email_resend_api_key,
          sender_email: config.email_sender_email,
          sender_name: config.email_sender_name,
          admin_email: config.email_admin_email,
        }
      });

      if (error) throw error;

      toast({
        title: "Correo de prueba enviado",
        description: `Se envió un correo de prueba a ${config.email_admin_email}`,
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: "Error al enviar correo",
        description: "No se pudo enviar el correo de prueba. Verifica tu configuración.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-96 bg-muted rounded-lg"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Configuración de Correo
          </h1>
          <p className="text-muted-foreground mt-2">
            Configura el servicio de correo electrónico usando Resend
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Configuración de Resend</CardTitle>
                <CardDescription>
                  Configura tu API key de Resend y los detalles del remitente
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Necesitas una cuenta en{" "}
                <a 
                  href="https://resend.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  resend.com
                </a>{" "}
                y crear un API key en{" "}
                <a 
                  href="https://resend.com/api-keys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  resend.com/api-keys
                </a>
              </AlertDescription>
            </Alert>

            {/* Email Enabled Switch */}
            <div className="flex items-center justify-between">
              <Label htmlFor="email-enabled" className="text-sm font-medium">
                Activar servicio de correo
              </Label>
              <Switch
                id="email-enabled"
                checked={config.email_enabled}
                onCheckedChange={(checked) => 
                  setConfig(prev => ({ ...prev, email_enabled: checked }))
                }
              />
            </div>

            <Separator />

            {/* API Key */}
            <div className="space-y-2">
              <Label htmlFor="api-key">Resend API Key</Label>
              <Input
                id="api-key"
                type="password"
                placeholder="re_..."
                value={config.email_resend_api_key}
                onChange={(e) => setConfig(prev => ({ ...prev, email_resend_api_key: e.target.value }))}
              />
            </div>

            {/* Sender Email */}
            <div className="space-y-2">
              <Label htmlFor="sender-email">Correo del Remitente</Label>
              <Input
                id="sender-email"
                type="email"
                placeholder="noreply@tudominio.com"
                value={config.email_sender_email}
                onChange={(e) => setConfig(prev => ({ ...prev, email_sender_email: e.target.value }))}
              />
            </div>

            {/* Sender Name */}
            <div className="space-y-2">
              <Label htmlFor="sender-name">Nombre del Remitente</Label>
              <Input
                id="sender-name"
                type="text"
                placeholder="Tu Empresa"
                value={config.email_sender_name}
                onChange={(e) => setConfig(prev => ({ ...prev, email_sender_name: e.target.value }))}
              />
            </div>

            {/* Admin Email */}
            <div className="space-y-2">
              <Label htmlFor="admin-email">Correo del Administrador</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@tudominio.com"
                value={config.email_admin_email}
                onChange={(e) => setConfig(prev => ({ ...prev, email_admin_email: e.target.value }))}
              />
              <p className="text-sm text-muted-foreground">
                A este correo llegarán las notificaciones del sistema
              </p>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={saveConfig}
                disabled={saving}
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>

              <Button
                onClick={sendTestEmail}
                disabled={testing || !config.email_enabled}
                variant="outline"
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                {testing ? 'Enviando...' : 'Enviar Correo de Prueba'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailSettings;